import { useDragula } from '@beesoft/react-dragula';
import { useSignal, useSignalEffect } from '@preact/signals-react';
import clsx from 'clsx';
import { cloneDeep, debounce, isEqual } from 'lodash';
import type { Ref } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { v4 } from 'uuid';
import type { OdinDataRetrieval } from '../../common/data-retrieval/data-retrieval-interfaces';
import { combineRefs } from '../../common/functions/combine-refs';
import { useEvent } from '../../common/hooks/use-event';
import useOdinMemo from '../../common/hooks/use-odin-memo';
import type { OnResizeFunctionParameters } from '../../common/hooks/use-resize-observer';
import useResizeObserver from '../../common/hooks/use-resize-observer';
import useTestTag from '../../common/hooks/use-test-tag';
import type { OdinKey } from '../../common/types/odin-key.type';
import { getAttributeValueRecursive, hasClassNameRecursive } from '../common-functions';
import type {
  ContinuousScrollerFilter,
  ContinuousScrollerSort,
} from '../continuous-scroller/continuous-scroller-types';
import type { JsonData } from '../generic-types';
import { Api } from './api/api';
import type { DefaultDataGridMenuItems } from './data-grid-cog-menu.component';
import DataGridCogMenu from './data-grid-cog-menu.component';
import DataGridData from './data-grid-data.component';
import type { RowTemplateType } from './data-grid-data-row.component';
import { calculateRowWidth, getColumnMaxWidth, getColumnMinWidth, setColumnDefaultValues } from './data-grid-functions';
import DataGridHeader from './data-grid-header.component';
import type { DataGridColumnSettings, DataGridRowIndicator, DataGridSettings } from './data-grid-settings.interface';
import type { DataGridSettingsMenuItem } from './data-grid-settings-menu.component';
import { changeSignalColumnSize } from './functions/data-grid-signal-functions';

export interface DataGridProps {
  gridSettings: DataGridSettings;
  data: JsonData | OdinDataRetrieval;
  customData?: JsonData;
  rowIndicator?: DataGridRowIndicator;
  rowTemplate?: RowTemplateType;
  showSettings?: boolean;
  /**
   * Event handler that fires when a row is selected or unselected.
   * @param gridId {string} - The unique Id for this grid component; used when multiple grid are on a single page.
   * @param selectedRows {JsonData} - The data representing the selected rows.
   */
  onRowSelectionChange?: (gridId: string, selectedRows: JsonData) => void;
  onCogMenuItemClicked?: (itemName: string) => void;
  onViewChange?: (hasChanged: boolean) => void;
  customSettingsMenuItems?: Array<DataGridSettingsMenuItem>;
  visibleDefaultSettings?: Array<DefaultDataGridMenuItems>;
  showGridColumnsOnSettingsMenu?: boolean;
  showGridMenu?: boolean;
  isPaginated?: boolean;
}

export interface DataGridRef {
  api: Api;
}

function DataGrid(props: DataGridProps, ref: Ref<DataGridRef>) {
  const {
    gridSettings,
    data,
    customData,
    rowIndicator,
    rowTemplate,
    showSettings = true,
    onRowSelectionChange,
    onCogMenuItemClicked,
    onViewChange,
    customSettingsMenuItems,
    visibleDefaultSettings,
    showGridColumnsOnSettingsMenu = true,
    showGridMenu,
    isPaginated = true,
  } = props;

  const [columnsChanged, setColumnsChanged] = useState(false);

  const gridId = useRef(v4());
  const forceResetColumnMap = useRef<Map<OdinKey, boolean>>(new Map());
  const columnWidthAdjustedRef = useRef(false);

  const initialFieldFilters = useOdinMemo(
    () => [...(gridSettings.initialFieldFilters ?? [])],
    [gridSettings.initialFieldFilters]
  );

  // these are replacing the same items in the reducer data
  const originalColumns = useRef<Array<DataGridColumnSettings>>([]);

  const gridScroller = useSignal<HTMLElement | null>(null);
  const filterSignal = useSignal<Array<ContinuousScrollerFilter>>(initialFieldFilters);
  const sortSignal = useSignal<Array<ContinuousScrollerSort>>(gridSettings.initialSortedFields || []);

  const tagCreator = useTestTag('data-grid');

  const handleWindowResize = (width?: number) => {
    // Get width from element if not provided
    const actualWidth = width ?? gridScroller.value?.clientWidth;

    if (gridSettings.autoSizeColumns && actualWidth) {
      changeSignalColumnSize(
        columns,
        rowWidth,
        indicatorRowWidth,
        availableWidth,
        [], // Window resize uses proportional redistribution (not individual column updates like manual resize)
        actualWidth
      );
    } else if (availableWidth.value === 0 && actualWidth) {
      availableWidth.value = actualWidth - (indicatorRowWidth.value ?? 0);
    }
  };

  const debounceHandleWindowResize = debounce((width: number) => {
    handleWindowResize(width);
  }, 100);

  const handleResize = useEvent(({ width }: OnResizeFunctionParameters) => {
    if (width) {
      debounceHandleWindowResize(width);
    }
  });

  const columns = useSignal(setColumnDefaultValues(gridSettings.columns, gridSettings.autoSizeColumns));
  const rowWidth = useSignal(calculateRowWidth(gridSettings.columns));
  const indicatorRowWidth = useSignal(0);
  const availableWidth = useSignal(0);
  const staticData = useSignal<JsonData>([]);
  const selectedRows = useSignal<JsonData>([]);
  const highlightedRows = useSignal<JsonData>([]);
  const pageSize = useSignal(gridSettings.initialPageSize || 50);
  const [optionsContainerRef] = useResizeObserver(handleResize);

  // this is defined here, so it can be used in the useDragula hook below. If not it will have an error about a function
  // not defined before its used.
  const movesListener = useCallback((_element?: Element, _container?: Element, handle?: Element) => {
    return (
      handle !== undefined &&
      hasClassNameRecursive(handle, 'column-main') &&
      getAttributeValueRecursive(handle, 'data-draggable') === 'true'
    );
  }, []);

  useDragula([], {
    moves: movesListener,
  });

  const resetView = useEvent(() => {
    columnWidthAdjustedRef.current = false;

    forceResetColumnMap.current.clear();

    gridSettings.columns.forEach((column) => {
      forceResetColumnMap.current.set(column.id, true);
    });

    originalColumns.current = cloneDeep(gridSettings.columns);
    columns.value = originalColumns.current;
    sortSignal.value = gridSettings.initialSortedFields || [];
    filterSignal.value = initialFieldFilters || [];
  });

  const apiRef = useRef<Api>(
    new Api(resetView, filterSignal, sortSignal, columns, staticData, selectedRows, highlightedRows)
  );

  useSignalEffect(() => {
    setColumnsChanged(
      gridSettings.columns.some(
        (column, index) => column.id !== columns.value[index]?.id || column.visible !== columns.value[index]?.visible
      )
    );
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only trigger on gridSettings.columns change, other deps are stable signals or props that shouldn't trigger re-run
  useEffect(() => {
    if (!isEqual(gridSettings.columns, originalColumns.current)) {
      const newColumns = cloneDeep(gridSettings.columns);

      // Preserve auto-sized widths by merging from current columns
      if (columnWidthAdjustedRef.current && columns.value.length > 0) {
        for (const newColumn of newColumns) {
          const currentColumn = columns.value.find((col) => col.id === newColumn.id);
          if (currentColumn && currentColumn.width) {
            // Clamp to new column's min/max constraints
            const minWidth = getColumnMinWidth(newColumn);
            const maxWidth = getColumnMaxWidth(newColumn);
            newColumn.width = Math.max(minWidth, Math.min(currentColumn.width, maxWidth));
          }
        }
      }

      originalColumns.current = newColumns;
      columns.value = newColumns;
      onViewChange?.(false);
      filterSignal.value = initialFieldFilters || [];
      sortSignal.value = gridSettings.initialSortedFields || [];
    }
  }, [gridSettings.columns]);

  useSignalEffect(() => {
    const sortChanged = !isEqual(sortSignal.value, gridSettings.initialSortedFields);
    const filtersChanged = !isEqual(filterSignal.value, initialFieldFilters);

    onViewChange?.(columnsChanged || sortChanged || filtersChanged);
  });

  useSignalEffect(() => {
    onRowSelectionChange?.(gridId.current, selectedRows.value);
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: gridScroller and staticData are signals, only re-run when data prop changes
  useEffect(() => {
    gridScroller.value?.scrollTo(0, 0);

    if (Array.isArray(data)) {
      staticData.value = data;
    }
  }, [data]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: gridSettings.autoSizeColumns is stable, handleWindowResize is a local function that shouldn't trigger re-runs
  useEffect(() => {
    if (gridSettings.autoSizeColumns) {
      handleWindowResize();
    }
  }, [availableWidth.value, columnsChanged]);

  const onGridElementCreated = useCallback(
    debounce((element: HTMLElement) => {
      if (gridScroller.value === null || !gridScroller.value.isSameNode(element)) {
        gridScroller.value = element;
      }
    }, 75),
    []
  );

  useImperativeHandle(ref, () => ({
    api: apiRef.current,
  }));

  const dynamicProperties = useOdinMemo(() => {
    const properties: Record<string, unknown> = {};

    if (!Array.isArray(data)) {
      properties['search'] = data;
    }

    return properties;
  }, [data]);

  const wrapperClasses = clsx('w-full text-lg overflow-hidden relative mt-2', {
    'pr-10': showSettings,
    'h-full max-h-[inherit]': gridSettings.fullHeight === true,
    'h-80': !gridSettings.fullHeight,
  });
  const gridClasses = clsx('w-full h-full max-h-[inherit] overflow-auto custom-scroll');

  const gridElementRef = combineRefs((element) => {
    if (element) onGridElementCreated(element);
  }, tagCreator('wrapper'));

  return (
    <div className={wrapperClasses} ref={tagCreator()}>
      <div ref={gridElementRef} className={gridClasses}>
        <DataGridHeader
          columns={columns}
          rowWidth={rowWidth}
          indicatorRowWidth={indicatorRowWidth}
          availableWidth={availableWidth}
          filterSignal={filterSignal}
          sortSignal={sortSignal}
          scrollDetection={gridSettings.scrollDetection}
          useIndicatorColumn={rowIndicator !== undefined}
          headerRowIndicator={gridSettings.headerRowIndicator}
          forceResetColumnMap={forceResetColumnMap.current}
          {...dynamicProperties}
        />
        <DataGridData
          columns={columns}
          selectedRows={selectedRows}
          highlightedRows={highlightedRows}
          staticData={staticData}
          pageSize={pageSize}
          optionsContainerRef={optionsContainerRef}
          components={gridSettings.components}
          rowIndicator={rowIndicator}
          filterLocation={gridSettings.filterLocation}
          data={data}
          customData={customData}
          minimumPageCount={gridSettings.minimumPageCount}
          stopRequestsOnNoData={gridSettings.stopRequestsOnNoData}
          rowIsClickable={gridSettings.rowIsClickable}
          sortFunction={gridSettings.sortFunction}
          filterSignal={filterSignal}
          sortSignal={sortSignal}
          api={apiRef.current}
          scrollerElement={gridScroller}
          rowTemplate={rowTemplate}
          allowCellScrollBar={gridSettings.allowCellScrollBar}
          columnWidthAdjustedRef={columnWidthAdjustedRef}
          isPaginated={isPaginated}
          autoSizeColumns={gridSettings.autoSizeColumns}
        />
      </div>

      {showSettings && (
        <DataGridCogMenu
          columns={columns}
          rowWidth={rowWidth}
          indicatorRowWidth={indicatorRowWidth}
          filters={filterSignal}
          resetView={resetView}
          onCogMenuItemClicked={onCogMenuItemClicked}
          customSettingsMenuItems={customSettingsMenuItems}
          visibleDefaultSettings={visibleDefaultSettings}
          showGridColumnsOnSettingsMenu={showGridColumnsOnSettingsMenu}
          api={apiRef.current}
          autoSizeColumns={gridSettings.autoSizeColumns}
          usesIndicatorColumn={rowIndicator !== undefined}
          showGridMenu={showGridMenu}
        />
      )}
    </div>
  );
}

export default forwardRef(DataGrid);
